import React, { useEffect, useState } from 'react';
import { element } from 'prop-types';
import { AuthContext } from './auth';

const BE_TOKEN_STORAGE_KEY = 'dd-be-token';

/**
 * Redireciona para o logout do Keycloak para encerrar a sessão SSO.
 * Evita que, ao sair do fluxo Be, o usuário volte logado no Keycloak.
 */
const redirectToKeycloakLogout = () => {
  try {
    const creds = JSON.parse(process.env.REACT_APP_PORTAL_URL || '{}');
    const baseUrl = creds['auth-server-url']?.replace(/\/$/, '');
    const realm = creds.realm;
    const clientId = creds.resource;
    if (baseUrl && realm && clientId) {
      const redirectUri = encodeURIComponent(window.location.origin);
      window.location.href = `${baseUrl}/realms/${realm}/protocol/openid-connect/logout?client_id=${clientId}&post_logout_redirect_uri=${redirectUri}`;
      return true;
    }
  } catch (e) {
    console.warn('[BeAuth] Não foi possível redirecionar para logout Keycloak:', e);
  }
  return false;
};

/**
 * Extrai o token da URL (query param ou hash).
 * Suporta 'token' e 'jwt' para flexibilidade com o Becompliance.
 * Alguns fluxos OAuth usam o fragment (#token=xxx) em vez da query (?token=xxx).
 * @returns {string|null}
 */
const extractTokenFromUrl = () => {
  const { search, hash } = window.location;

  const fromSearch = new URLSearchParams(search);
  const tokenFromSearch = fromSearch.get('token') || fromSearch.get('jwt');
  if (tokenFromSearch) return tokenFromSearch;

  const hashStr = hash ? hash.replace(/^#/, '') : '';
  if (hashStr) {
    try {
      const fromHash = new URLSearchParams(hashStr);
      const tokenFromHash = fromHash.get('token') || fromHash.get('jwt');
      if (tokenFromHash) return tokenFromHash;
    } catch (e) {
      const match = hashStr.match(/(?:^|&)(?:token|jwt)=([^&]+)/);
      if (match) return decodeURIComponent(match[1]);
    }
  }

  return null;
};

/**
 * Remove o token da URL por segurança (evita logs, histórico, referrer).
 */
const cleanTokenFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  url.searchParams.delete('jwt');
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  hashParams.delete('token');
  hashParams.delete('jwt');
  const newHash = hashParams.toString() ? `#${hashParams.toString()}` : '';
  window.history.replaceState({}, document.title, url.pathname + url.search + newHash);
};

/**
 * Provider de autenticação para o fluxo Becompliance → RiskCheck.
 * Único ponto de extração do token: query (?token=, ?jwt=) ou hash (#token=, #jwt=).
 * Armazena em sessionStorage e fornece a mesma interface do AuthContext (compatível com useAuth).
 */
export const BeAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    const fromUrl = extractTokenFromUrl();
    if (fromUrl) {
      sessionStorage.setItem(BE_TOKEN_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(BE_TOKEN_STORAGE_KEY);
  });

  useEffect(() => {
    const fromUrl = extractTokenFromUrl();
    if (fromUrl) {
      sessionStorage.setItem(BE_TOKEN_STORAGE_KEY, fromUrl);
      setToken(fromUrl);
    }
    cleanTokenFromUrl();
  }, []);

  const logout = () => {
    sessionStorage.removeItem(BE_TOKEN_STORAGE_KEY);
    localStorage.removeItem('dd-token');
    setToken(null);
    if (!redirectToKeycloakLogout()) {
      window.location.reload();
    }
  };

  const resolvedToken = token || sessionStorage.getItem(BE_TOKEN_STORAGE_KEY);

  const values = {
    token: resolvedToken,
    isAuthenticated: !!resolvedToken,
    logout,
    login: () => {}, // Não utilizado no fluxo Becompliance
  };

  return (
    <AuthContext.Provider value={values}>
      {children}
    </AuthContext.Provider>
  );
};

BeAuthProvider.propTypes = {
  children: element.isRequired,
};
